/**
 * Artigos do blog: segmentos (pizzaria, restaurante, confeitaria, barbearia)
 */

import type { BlogArticle } from '../types';

const PUBLISHED = '2026-09-27';

export const SEGMENTOS_ARTICLES: BlogArticle[] = [
  {
    slug: 'pizza-meio-a-meio-como-cobrar',
    title: 'Pizza meio a meio: cobrar pelo maior preço ou pela média?',
    description:
      'Como cobrar pizza meio a meio: pelo sabor mais caro ou pela média, prós e contras de cada regra, bordas, tamanhos e como evitar erro no pedido.',
    excerpt:
      'Maior preço ou média? Veja como cada regra afeta a margem e a percepção do cliente, e como evitar o meio a meio cobrado errado.',
    category: 'Segmentos',
    keywords: [
      'pizza meio a meio como cobrar',
      'preço pizza meio a meio',
      'sistema para pizzaria',
      'cardápio digital pizzaria',
      'pizza dois sabores preço',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    relatedSegment: 'pizzaria',
    blocks: [
      {
        type: 'p',
        text: 'Toda pizzaria precisa decidir como cobrar a pizza de dois (ou mais) sabores. As duas regras mais usadas no Brasil são **cobrar pelo sabor mais caro** e **cobrar pela média dos sabores**.',
      },
      {
        type: 'table',
        head: ['Regra', 'Vantagem', 'Cuidado'],
        rows: [
          [
            'Maior preço',
            'Protege a margem: o sabor premium nunca sai com desconto',
            'Cliente pode achar caro se misturar um sabor simples com um especial',
          ],
          [
            'Média',
            'Parece mais justo para o cliente',
            'Reduz a margem quando um dos sabores é bem mais caro',
          ],
        ],
      },
      { type: 'h2', text: 'Como decidir' },
      {
        type: 'ul',
        items: [
          'Se a diferença de preço entre os sabores é pequena, a média é simples e agrada.',
          'Se você tem sabores premium com ingredientes caros, o maior preço protege o lucro.',
          'Seja qual for a regra, **deixe claro no cardápio**: "meio a meio cobrado pelo sabor de maior valor".',
        ],
      },
      { type: 'h2', text: 'Bordas, tamanhos e limites' },
      {
        type: 'p',
        text: 'Defina o número máximo de sabores por tamanho (por exemplo, 2 na média e 3 na grande), a borda como adicional com preço por tamanho e os complementos opcionais com limite. Assim o pedido já sai calculado certo.',
      },
      {
        type: 'tip',
        title: 'O erro mais caro',
        text: 'Quando o meio a meio é calculado pelo atendente, cada um cobra de um jeito. Com o cardápio digital, a regra é aplicada sozinha em todos os pedidos.',
      },
      {
        type: 'p',
        text: 'No Vende Aí, cada tamanho tem o limite de sabores e a regra de preço (maior valor ou média), e bordas e adicionais são somados automaticamente. Veja tudo o que o sistema faz para [pizzarias](/para/pizzaria).',
      },
      {
        type: 'cta',
        text: 'Configure seu meio a meio no Vende Aí e teste por 30 dias.',
        segment: 'PIZZERIA',
      },
    ],
    faq: [
      {
        question: 'Como cobrar pizza meio a meio?',
        answer:
          'As regras mais comuns são cobrar pelo sabor de maior valor ou pela média dos sabores. O importante é informar a regra no cardápio e usar um sistema que aplique o cálculo automaticamente.',
      },
    ],
  },
  {
    slug: 'qr-code-na-mesa-restaurante',
    title: 'QR Code na mesa: vale a pena para restaurante pequeno?',
    description:
      'Como funciona o pedido pelo QR Code na mesa, vantagens para restaurantes pequenos, conta paga pelo celular e cuidados na implantação.',
    excerpt:
      'O cliente pede e paga pelo celular, sem esperar o garçom. Veja quando o QR Code na mesa vale a pena e como implantar.',
    category: 'Segmentos',
    keywords: [
      'QR Code na mesa',
      'cardápio QR Code restaurante',
      'pedido pela mesa celular',
      'autoatendimento restaurante',
      'comanda digital',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    relatedSegment: 'restaurante',
    blocks: [
      {
        type: 'p',
        text: 'Com o **QR Code na mesa**, o cliente aponta a câmera do celular, abre o cardápio no navegador e faz o pedido, que chega direto na cozinha. Não precisa baixar aplicativo.',
      },
      { type: 'h2', text: 'Vantagens para quem tem salão pequeno' },
      {
        type: 'ul',
        items: [
          '**Menos espera:** o cliente pede na hora que quiser, inclusive a segunda rodada de bebida.',
          '**Menos erro:** o pedido chega escrito como o cliente escolheu.',
          '**Equipe enxuta:** o garçom foca em servir e atender, não em anotar.',
          '**Conta mais rápida:** o cliente pode pagar pelo celular, inclusive dividindo a conta.',
        ],
      },
      { type: 'h2', text: 'Cuidados na implantação' },
      {
        type: 'ol',
        items: [
          'Cada mesa com o seu QR Code, para o pedido cair na mesa certa.',
          'Cardápio com fotos e descrições claras (o cliente não tem a quem perguntar).',
          'Equipe treinada para ajudar quem prefere pedir do jeito tradicional.',
          'Wi-Fi ou sinal de celular razoável no salão.',
        ],
      },
      {
        type: 'p',
        text: 'No Vende Aí, cada mesa ganha um QR Code; o pedido cai na hora no painel e na cozinha, e a conta pode ser paga por PIX direto da mesa, com pagamento parcial e liberação automática da mesa. Veja mais para [restaurantes](/para/restaurante).',
      },
      {
        type: 'cta',
        text: 'Coloque o QR Code nas suas mesas com o Vende Aí. 30 dias grátis.',
        segment: 'RESTAURANT',
      },
    ],
    faq: [
      {
        question: 'O cliente precisa baixar aplicativo para pedir pelo QR Code?',
        answer: 'Não. O QR Code abre o cardápio no navegador do celular.',
      },
      {
        question: 'Dá para dividir a conta pelo QR Code?',
        answer:
          'Depende do sistema. No Vende Aí a conta pode ser paga em partes pelo celular, e a mesa é liberada quando o total é quitado.',
      },
    ],
  },
  {
    slug: 'encomendas-de-bolo-como-organizar',
    title: 'Encomendas de bolo e doces: como organizar a produção sem caderno',
    description:
      'Como organizar encomendas de confeitaria e padaria: agenda com data e horário, limite de produção por dia, pagamento antecipado e controle de insumos.',
    excerpt:
      'Encomenda esquecida é cliente perdido. Veja como organizar pedidos com data, limite de produção e pagamento antecipado.',
    category: 'Segmentos',
    keywords: [
      'como organizar encomendas confeitaria',
      'sistema para confeitaria',
      'sistema para padaria',
      'encomenda de bolo online',
      'agenda de encomendas',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    relatedSegment: 'padaria',
    blocks: [
      {
        type: 'p',
        text: 'Em confeitarias e padarias, o problema raramente é vender: é **organizar**. Encomendas anotadas em cadernos, no WhatsApp e em papéis soltos terminam em bolo esquecido, data trocada e madrugada de produção.',
      },
      { type: 'h2', text: 'Os 4 pilares de uma agenda de encomendas' },
      {
        type: 'ol',
        items: [
          '**Tudo num lugar só:** cada encomenda com produto, data, horário, retirada ou entrega e observações.',
          '**Antecedência mínima:** por exemplo, bolos com 48 horas de antecedência.',
          '**Limite de produção:** um número máximo de encomendas por dia ou por horário.',
          '**Pagamento antecipado:** total ou sinal, para reduzir desistências.',
        ],
      },
      { type: 'h2', text: 'Preço por quantidade' },
      {
        type: 'p',
        text: 'Cento de salgados, caixas de doces e kits festa ficam mais atraentes com **preço que diminui conforme a quantidade**. Deixe as faixas visíveis no cardápio.',
      },
      {
        type: 'tip',
        title: 'Controle de insumos',
        text: 'Com ficha técnica, cada encomenda confirmada já mostra quanto de farinha, chocolate e embalagem vai sair do estoque.',
      },
      {
        type: 'p',
        text: 'No Vende Aí, o cliente agenda pelo link, escolhendo data e um horário disponível; você define antecedência mínima, prazo máximo e limite por dia, por horário ou pela capacidade da equipe, e recebe o pagamento antecipado por PIX ou cartão. Veja mais para [padarias e confeitarias](/para/padaria).',
      },
      {
        type: 'cta',
        text: 'Organize suas encomendas no Vende Aí, com 30 dias grátis.',
        segment: 'BAKERY',
      },
    ],
    faq: [
      {
        question: 'Como organizar encomendas de confeitaria?',
        answer:
          'Centralize todas as encomendas num sistema com data e horário, defina antecedência mínima e limite de produção por dia, e cobre antecipado para reduzir desistências.',
      },
    ],
  },
  {
    slug: 'agenda-online-para-barbearia',
    title: 'Agenda online para barbearia: como sair do WhatsApp e reduzir faltas',
    description:
      'Como funciona uma agenda online para barbearia, como organizar horários pela escala da equipe, reduzir faltas com pagamento antecipado e vender produtos.',
    excerpt:
      'Parar o corte para responder "tem horário hoje?" custa tempo e dinheiro. Veja como uma agenda online organiza a barbearia.',
    category: 'Segmentos',
    keywords: [
      'agenda online barbearia',
      'sistema para barbearia',
      'agendamento online barbearia',
      'como reduzir faltas barbearia',
      'aplicativo de agendamento barbearia',
    ],
    publishedAt: PUBLISHED,
    updatedAt: PUBLISHED,
    relatedSegment: 'barbearia',
    blocks: [
      {
        type: 'p',
        text: 'Numa barbearia, cada minuto de cadeira vazia é faturamento perdido, e cada interrupção para responder mensagem atrasa o próximo cliente. Uma **agenda online** resolve os dois problemas: o cliente escolhe o horário sozinho e você vê o dia organizado.',
      },
      { type: 'h2', text: 'Como funciona' },
      {
        type: 'ol',
        items: [
          'Você cadastra os serviços com duração e preço (corte, barba, combo).',
          'Define os dias, os horários e o almoço de cada barbeiro, e o que cada um faz.',
          'O cliente abre o link, escolhe o serviço, o dia e um horário livre.',
          'A confirmação chega pelo WhatsApp.',
        ],
      },
      { type: 'h2', text: 'Como reduzir faltas' },
      {
        type: 'ul',
        items: [
          '**Pagamento antecipado** (total ou do serviço principal): quem paga antes costuma faltar menos.',
          '**Confirmação clara** com data, horário e endereço.',
          '**Política simples** de remarcação, informada no agendamento.',
        ],
      },
      { type: 'h2', text: 'Venda além do corte' },
      {
        type: 'p',
        text: 'Pomadas, óleos e produtos de barba podem ficar na mesma loja online, com retirada na hora do corte. Programas de fidelidade (por exemplo, um corte grátis depois de certo número de visitas) e mensagens para quem não volta há algumas semanas completam a estratégia.',
      },
      {
        type: 'p',
        text: 'No Vende Aí, a agenda usa os horários da escala e das especialidades de cada barbeiro, o cliente pode pagar antecipado por PIX ou cartão e recebe a confirmação pelo WhatsApp. Veja tudo para [barbearias](/para/barbearia).',
      },
      {
        type: 'cta',
        text: 'Coloque a agenda da sua barbearia online. Teste o Vende Aí por 30 dias.',
        segment: 'BARBERSHOP',
      },
    ],
    faq: [
      {
        question: 'Como funciona agenda online para barbearia?',
        answer:
          'O cliente abre o link da barbearia, escolhe o serviço, o dia e um horário livre, calculado pela escala da equipe, e recebe a confirmação. Você vê a agenda do dia organizada no painel.',
      },
      {
        question: 'Como diminuir as faltas na barbearia?',
        answer:
          'Cobrar antecipado no agendamento, enviar confirmação clara e ter uma política simples de remarcação são as medidas mais eficazes.',
      },
    ],
  },
];
